import * as THREE from 'three';
import { createLogger } from '@/utils/logger';
import type { ModelValidationReport } from '@/types';

const log = createLogger('validator');

/**
 * Traverses a loaded model and produces a validation report: mesh/vertex/
 * triangle counts, draw-call estimate, unique materials & textures, animations,
 * cameras, emissive materials, and missing-UV / missing-normal warnings. The
 * report is printed to the console and returned for the debug panel.
 */
export function validateModel(
  root: THREE.Object3D,
  animations: THREE.AnimationClip[],
  cameras: THREE.Camera[],
): ModelValidationReport {
  let meshCount = 0;
  let vertexCount = 0;
  let triangleCount = 0;
  let drawCallEstimate = 0;

  const materials = new Set<THREE.Material>();
  const textures = new Set<THREE.Texture>();
  const emissiveMaterials: string[] = [];
  const missingUVs: string[] = [];
  const missingNormals: string[] = [];

  root.traverse((child) => {
    const mesh = child as THREE.Mesh;
    if (!mesh.isMesh) return;

    meshCount += 1;
    const geometry = mesh.geometry;
    const position = geometry.getAttribute('position');
    if (position) {
      vertexCount += position.count;
      triangleCount += geometry.index ? geometry.index.count / 3 : position.count / 3;
    }

    const name = mesh.name || '(unnamed)';
    if (!geometry.getAttribute('normal')) missingNormals.push(name);

    const meshMaterials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    drawCallEstimate += meshMaterials.length;

    for (const material of meshMaterials) {
      if (!material) continue;
      materials.add(material);

      for (const key of Object.keys(material)) {
        const value = (material as unknown as Record<string, unknown>)[key];
        if (value instanceof THREE.Texture) textures.add(value);
      }

      const std = material as THREE.MeshStandardMaterial;
      if (std.map && !geometry.getAttribute('uv')) missingUVs.push(name);
      const emissive =
        (std.emissive && std.emissive.r + std.emissive.g + std.emissive.b > 0) || !!std.emissiveMap;
      if (emissive) emissiveMaterials.push(material.name || name);
    }
  });

  const warnings: string[] = [];
  if (meshCount === 0) warnings.push('No meshes found in model.');
  if (missingUVs.length) warnings.push(`${missingUVs.length} mesh(es) use a map but lack UVs.`);
  if (missingNormals.length) {
    warnings.push(`${missingNormals.length} mesh(es) lack normals (lighting/shading issues).`);
  }
  if (drawCallEstimate > 200) {
    warnings.push(`High draw-call estimate (${drawCallEstimate}); consider merging/instancing.`);
  }

  const box = new THREE.Box3().setFromObject(root);
  const size = box.getSize(new THREE.Vector3());
  const center = box.getCenter(new THREE.Vector3());

  const report: ModelValidationReport = {
    objectName: root.name || 'scene',
    meshCount,
    vertexCount,
    triangleCount: Math.round(triangleCount),
    drawCallEstimate,
    materialCount: materials.size,
    textureCount: textures.size,
    animationCount: animations.length,
    animationNames: animations.map((clip) => clip.name),
    hasCamera: cameras.length > 0,
    cameraNames: cameras.map((camera) => camera.name),
    emissiveMaterials,
    missingUVs,
    missingNormals,
    warnings,
    bounds: {
      size: [size.x, size.y, size.z],
      center: [center.x, center.y, center.z],
    },
  };

  printReport(report);
  return report;
}

function printReport(report: ModelValidationReport): void {
  log.group(`model validation — "${report.objectName}"`);
  console.table({
    meshes: report.meshCount,
    vertices: report.vertexCount,
    triangles: report.triangleCount,
    drawCalls: report.drawCallEstimate,
    materials: report.materialCount,
    textures: report.textureCount,
    animations: report.animationCount,
    cameras: report.cameraNames.length,
  });
  if (report.animationNames.length) log.info('animations:', report.animationNames);
  if (report.cameraNames.length) log.info('cameras:', report.cameraNames);
  if (report.emissiveMaterials.length) log.info('emissive materials:', report.emissiveMaterials);
  log.info(
    'bounds size:',
    report.bounds.size.map((n) => Number(n.toFixed(3))),
  );
  for (const warning of report.warnings) log.warn(warning);
  log.groupEnd();
}
